import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Loader2,
  Search,
  Plus,
  MoreVertical,
  Users as UsersIcon,
  Trash2,
  ArrowUpRight,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { hasRole } from "@/lib/roles";
import { ContactCreateModal } from "@/components/ContactCreateModal";

const STAGE_LABELS = {
  en_attente: "En attente",
  contacte: "Contacté",
  rdv_fixe: "RDV fixé",
  devis_envoye: "Devis envoyé",
  en_negociation: "En négociation",
  signe: "Signé",
  perdu: "Perdu",
};

const STAGE_COLOR = {
  en_attente: "bg-[#E8DCC8]/15 text-[#E8DCC8] border-[#E8DCC8]/30",
  contacte: "bg-[#3D362E] text-[#F5EFE7] border-[#4E453B]",
  rdv_fixe: "bg-[#D99A2B]/15 text-[#D99A2B] border-[#D99A2B]/30",
  devis_envoye: "bg-[#C4553B]/15 text-[#E07A62] border-[#C4553B]/30",
  en_negociation: "bg-[#8B5CF6]/15 text-[#C4B5FD] border-[#8B5CF6]/40",
  signe: "bg-[#7A8450]/20 text-[#B8C784] border-[#7A8450]/40",
  perdu: "bg-[#A63D2F]/15 text-[#E67B6D] border-[#A63D2F]/30",
};

function relativeTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  const days = Math.floor(diff / 86400);
  if (days < 30) return `il y a ${days} j`;
  const months = Math.floor(days / 30);
  if (months < 12) return `il y a ${months} mois`;
  return d.toLocaleDateString("fr-BE");
}

function formatPhoneBE(raw) {
  if (!raw) return "";
  const s = String(raw).replace(/[^\d+]/g, "");
  const digits = s.startsWith("+32") ? "0" + s.slice(3) : s;
  const m = digits.match(/^(0\d{3})(\d{2})(\d{2})(\d{2})$/);
  return m ? `${m[1]} ${m[2]} ${m[3]} ${m[4]}` : raw;
}

export default function ContactsPage() {
  const { state } = useAuth();
  const nav = useNavigate();
  const isAdmin = hasRole(state?.user, ["admin"]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activityFilter, setActivityFilter] = useState("all"); // all | with_active | archived
  const [tagFilter, setTagFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("all"); // all | 7 | 30 | 90
  const [offset, setOffset] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const limit = 50;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });
    if (debouncedSearch) params.set("search", debouncedSearch);
    api
      .get(`/contacts?${params.toString()}`)
      .then((r) => {
        if (!alive) return;
        setItems(r.data.items || []);
        setTotal(r.data.total || 0);
      })
      .catch((e) => alive && toast.error(formatApiError(e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [debouncedSearch, offset]);

  const filteredItems = useMemo(() => {
    let out = items;
    if (activityFilter === "with_active") {
      out = out.filter((c) => (c.projects_count || 0) > 0 && c.last_project_stage && !["signe", "perdu"].includes(c.last_project_stage));
    } else if (activityFilter === "archived") {
      out = out.filter((c) => (c.projects_count || 0) === 0 || ["signe", "perdu"].includes(c.last_project_stage));
    }
    if (tagFilter.trim()) {
      const t = tagFilter.trim().toLowerCase();
      out = out.filter((c) => (c.tags || []).some((x) => x.toLowerCase().includes(t)));
    }
    if (dateFilter !== "all") {
      const days = parseInt(dateFilter, 10);
      const cutoff = Date.now() - days * 86400 * 1000;
      out = out.filter((c) => new Date(c.created_at).getTime() >= cutoff);
    }
    return out;
  }, [items, activityFilter, tagFilter, dateFilter]);

  const handleDelete = async (contact) => {
    if (!isAdmin) return;
    const label = `${contact.first_name || ""} ${contact.last_name || ""}`.trim() || contact.email || "ce contact";
    if (!window.confirm(`Supprimer ${label} ? Cette action est irréversible si le contact n'a aucun projet rattaché.`)) return;
    try {
      await api.delete(`/contacts/${contact.id}`);
      toast.success("Contact supprimé");
      setItems((prev) => prev.filter((c) => c.id !== contact.id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / limit));
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <div className="space-y-5" data-testid="contacts-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Contacts</h1>
          <p className="mt-1 text-sm text-slate-400">
            {total} contact{total > 1 ? "s" : ""} · fiches d'identité indépendantes des projets
          </p>
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          className="bg-[#C4553B] text-white hover:bg-[#B44A32]"
          data-testid="btn-new-contact"
        >
          <Plus className="mr-2 h-4 w-4" /> Nouveau contact
        </Button>
      </div>

      <Card className="border-slate-800 bg-slate-900/40">
        <CardContent className="grid grid-cols-1 gap-3 p-4 lg:grid-cols-[1fr_180px_180px_180px]">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setOffset(0);
              }}
              placeholder="Recherche nom, email, téléphone…"
              className="border-slate-700 bg-slate-950/50 pl-9 text-sm text-slate-100"
              data-testid="contacts-search"
            />
          </div>
          <Select value={activityFilter} onValueChange={setActivityFilter}>
            <SelectTrigger className="border-slate-700 bg-slate-950/50 text-sm" data-testid="filter-activity">
              <SelectValue placeholder="Activité" />
            </SelectTrigger>
            <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
              <SelectItem value="all">Tous les contacts</SelectItem>
              <SelectItem value="with_active">Avec projet actif</SelectItem>
              <SelectItem value="archived">Archivés / clos</SelectItem>
            </SelectContent>
          </Select>
          <Input
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            placeholder="Filtrer par tag…"
            className="border-slate-700 bg-slate-950/50 text-sm"
            data-testid="filter-tag"
          />
          <Select value={dateFilter} onValueChange={setDateFilter}>
            <SelectTrigger className="border-slate-700 bg-slate-950/50 text-sm" data-testid="filter-date">
              <SelectValue placeholder="Date création" />
            </SelectTrigger>
            <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
              <SelectItem value="all">Toutes dates</SelectItem>
              <SelectItem value="7">7 derniers jours</SelectItem>
              <SelectItem value="30">30 derniers jours</SelectItem>
              <SelectItem value="90">90 derniers jours</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
        </div>
      ) : filteredItems.length === 0 ? (
        <Card className="border-dashed border-slate-800 bg-transparent">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <UsersIcon className="mb-4 h-12 w-12 text-slate-600" />
            <p className="text-sm font-medium text-slate-300">Aucun contact ne correspond à ces critères.</p>
            <p className="mt-1 text-xs text-slate-500">Créez votre premier contact ou ajustez les filtres.</p>
            <Button
              onClick={() => setShowCreate(true)}
              variant="outline"
              className="mt-4 border-slate-700 bg-slate-800/50 text-slate-200 hover:bg-slate-800"
              data-testid="empty-create-contact"
            >
              <Plus className="mr-2 h-4 w-4" /> Créer votre premier contact
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-800 bg-slate-900/40">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="contacts-table">
              <thead className="border-b border-slate-800 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Nom</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Téléphone</th>
                  <th className="px-4 py-3">Ville</th>
                  <th className="px-4 py-3 text-center">Projets actifs</th>
                  <th className="px-4 py-3 text-center">Projets total</th>
                  <th className="px-4 py-3">Dernière activité</th>
                  <th className="w-10 px-2 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((c) => {
                  const displayName = `${c.first_name || ""} ${c.last_name || ""}`.trim() || "(sans nom)";
                  const activeCount =
                    (c.projects_count || 0) > 0 && c.last_project_stage && !["signe", "perdu"].includes(c.last_project_stage)
                      ? c.projects_count
                      : 0;
                  return (
                    <tr
                      key={c.id}
                      className="border-b border-slate-800/60 hover:bg-slate-800/30"
                      data-testid={`contact-row-${c.id}`}
                    >
                      <td className="px-4 py-3">
                        <button
                          onClick={() => nav(`/contacts/${c.id}`)}
                          className="text-left font-medium text-slate-100 hover:text-[#E07A62]"
                          data-testid={`contact-name-${c.id}`}
                        >
                          {displayName}
                          {c.company_name && (
                            <span className="block text-xs text-slate-500">{c.company_name}</span>
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-slate-300">
                        {c.email ? (
                          <a
                            href={`mailto:${c.email}`}
                            className="hover:text-[#E07A62]"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {c.email}
                          </a>
                        ) : (
                          <span className="text-slate-600">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-300">
                        {c.phone ? (
                          <a
                            href={`tel:${c.phone_normalized || c.phone}`}
                            className="hover:text-[#E07A62]"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {formatPhoneBE(c.phone)}
                          </a>
                        ) : (
                          <span className="text-slate-600">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-400">{c.address_city || <span className="text-slate-600">—</span>}</td>
                      <td className="px-4 py-3 text-center">
                        {activeCount > 0 ? (
                          <Badge className={`${STAGE_COLOR[c.last_project_stage] || "bg-slate-800 text-slate-300"} border`}>
                            {activeCount}
                          </Badge>
                        ) : (
                          <span className="text-slate-600">0</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center text-slate-300">{c.projects_count || 0}</td>
                      <td className="px-4 py-3 text-slate-400">{relativeTime(c.updated_at)}</td>
                      <td className="px-2 py-3">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              className="rounded p-1 text-slate-400 hover:bg-slate-800 hover:text-slate-100"
                              data-testid={`contact-menu-${c.id}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreVertical className="h-4 w-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="border-slate-700 bg-slate-900 text-slate-100">
                            <DropdownMenuItem onSelect={() => nav(`/contacts/${c.id}`)}>
                              <ArrowUpRight className="mr-2 h-4 w-4" /> Voir fiche
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => nav(`/contacts/${c.id}?tab=projects&create=1`)}>
                              <Plus className="mr-2 h-4 w-4" /> Créer un projet
                            </DropdownMenuItem>
                            {isAdmin && (
                              <DropdownMenuItem
                                className="text-red-300 focus:text-red-200"
                                onSelect={() => handleDelete(c)}
                              >
                                <Trash2 className="mr-2 h-4 w-4" /> Supprimer
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pageCount > 1 && (
            <div className="flex items-center justify-between border-t border-slate-800 px-4 py-3 text-xs text-slate-400">
              <span>
                Page {currentPage} / {pageCount} · {total} contacts
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  className="border-slate-700 bg-slate-800/50 text-slate-200"
                  data-testid="contacts-prev-page"
                >
                  Précédent
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={offset + limit >= total}
                  onClick={() => setOffset(offset + limit)}
                  className="border-slate-700 bg-slate-800/50 text-slate-200"
                  data-testid="contacts-next-page"
                >
                  Suivant
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      <ContactCreateModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(c) => {
          setShowCreate(false);
          nav(`/contacts/${c.id}`);
        }}
      />
    </div>
  );
}
